import { useState, useEffect } from "react";
import {
  CustomButton,
  CustomTextField,
  CustomNumberField,
  NavigateBack,
} from "../../../components";
import { Heading } from "../AppSettingsStyles";
import { Notify } from "../../../utils";
import { Constant } from "../../../config";
import { ServiceCodeService } from "../../../_service";
import { useLocation, useNavigate } from "react-router-dom";

type ErrorsType = {
  servicecode?: string;
  fixedAmount?: string;
  variableAmount?: string;
  serviceDescription?: string;
};

const AddOrEditServiceCode = () => {
  const [servicecode, setServiceCode] = useState<string>("");
  const [fixedAmount, setFixedAmount] = useState<number>(0.00); // Default to 0.00
  const [variableAmount, setVariableAmount] = useState<string>("");
  const [serviceDescription, setServiceDescription] = useState<string>("");
  const [errors, setErrors] = useState<ErrorsType>({});

  const navigate = useNavigate();
  const location = useLocation();

  const validateFields = (): boolean => {
    let newErrors: ErrorsType = {};

    if (!servicecode) {
      newErrors.servicecode = "Service code is required.";
    } else if (servicecode.length !== 6) {
      newErrors.servicecode = "Service code must be exactly 6 characters long.";
    } else if (!/^[a-zA-Z0-9]*$/.test(servicecode)) {
      newErrors.servicecode = "Service code must be alphanumeric.";
    }

    if (fixedAmount < 0) {
      newErrors.fixedAmount = "Fixed amount must be a positive number.";
    }


    if (!variableAmount) {
      newErrors.variableAmount = "Amount per employee is required.";
    } else if (isNaN(Number(variableAmount)) || parseFloat(variableAmount) < 0) {
      newErrors.variableAmount = "Amount per employee must be a positive number.";
    }

    if (!serviceDescription) {
      newErrors.serviceDescription = "Service description is required.";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };


  const saveButtonClick = () => {
    if (validateFields()) {
      if (location.pathname.split("/").pop() === "edit") {
        editServiceCode();
      } else {
        addServiceCode();
      }
    }
  };

  const addServiceCode = async () => {
    try {
      const { status } = await ServiceCodeService._addServiceCode({
        name: servicecode.toUpperCase(),
        fixed_amount: fixedAmount || 0.00,
        variable_amount: variableAmount,
        description: serviceDescription,
      });
      if (status === 201) {
        Notify("Service code added successfully", 1);
        navigate("/dashboard/manage-service-code");
      }
    } catch (error: any) {
      Notify(Constant.common.something_went_wrong, 0);
    }
  };

  const editServiceCode = async () => {
    try {
      const { status } = await ServiceCodeService._editServiceCode(
        {
          name: servicecode.toUpperCase(),
          fixed_amount: fixedAmount,
          variable_amount: variableAmount,
          description: serviceDescription,
        },
        location.state?.id
      );
      if (status === 200) {
        Notify("Service code edited successfully", 1);
        navigate("/dashboard/manage-service-code");
      }
    } catch (error: any) {
      Notify(Constant.common.something_went_wrong, 0);
    }
  };

  useEffect(() => {
    const path = location.pathname.split("/").pop();
    if (path === "edit" && location.state) {
      const { name, fixedAmount, variableAmount, description } = location.state;
      setServiceCode(name || "");
      setFixedAmount(fixedAmount !== undefined ? Number(fixedAmount) : 0.00); 
      setVariableAmount(variableAmount || "");
      setServiceDescription(description || "");
    }
  }, [location]);

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: '.5rem' }}>
        <NavigateBack />
        <Heading>{location.pathname.split("/").pop() === "edit" ? "Edit Service Code" : "Add Service Code"}</Heading>
      </div>

      <div style={{ margin: "40px 16px" }}>
        <CustomTextField
          title="Service Code"
          placeholder="Enter Service Code Here (must be only 6 alphanumeric in length)"
          value={servicecode.toUpperCase()}
          onChangeText={(value) => {
            if (/^[a-zA-Z0-9]*$/.test(value) && value.length <= 6) {
              setServiceCode(value);
            }
          }}
          disabled={location.pathname.split("/").pop() === "edit"}
          error={errors.servicecode}
        />

        <div style={{ display: 'flex', gap: '110px', marginTop: '10px' }}>
          <label>Service Description</label>
          <div>
            <textarea
              value={serviceDescription}
              onChange={(e) => setServiceDescription(e.target.value)}
              rows={4}
              placeholder="Enter Service Description"
              style={{
                borderRadius: "0.2rem",
                minWidth: '400px',
                padding: '2px',
                fontSize: '16px',
                border: '1px solid #ced4da',
              }}
            />
            {errors.serviceDescription && <div style={{ color: 'red', fontSize: "14px" }}>{errors.serviceDescription}</div>}

          </div>
        </div>


        <CustomNumberField
          title="Fixed amount"
          value={fixedAmount.toString()} // Convert number to string for input field
          onChangeText={(value) => {
            const numericValue = parseFloat(value);
            if (!isNaN(numericValue) && numericValue >= 0) {
              setFixedAmount(numericValue); // Store as number
            } else if (value === "") {
              setFixedAmount(0.00); // Reset to 0.00 if cleared
            }
          }}
          error={errors.fixedAmount}
        />


        <CustomNumberField
          title="Amount per employee"
          value={variableAmount}
          onChangeText={setVariableAmount}
          error={errors.variableAmount}
        />
      </div>

      <CustomButton title="Save" onClick={saveButtonClick} />
    </div>
  );
};

export default AddOrEditServiceCode;
